import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AsyncStorage from '@react-native-async-storage/async-storage';
import { useInterpret } from '@xstate/react';

import { useNoticeBannersStackContext } from '@sg/garnish';
import { type DietaryPropertyKind, type DietaryRestriction } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { DIETARY_RESTRICTIONS_STORAGE_KEY } from '../../../constants';
import { useUpdateCustomerPreferences } from '../../../hooks';
import { HAS_OPENED_DIETARY_PREFERENCES_MODAL_STORAGE_KEY } from '../DietaryRestrictions.constants';
import { dietaryRestrictionsMachine } from './dietaryRestrictionsMachine';
import { setMachinePersistedState } from './dietaryRestrictionsMachine.utils';
import { useDietaryRestrictionsFromAsyncStorage } from './hooks';

// ────────────────────────────────────────────────────────────────────────────

export const useDietaryRestrictionsInterpret = () => {
  const { updateCustomerPreferences } = useUpdateCustomerPreferences();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();

  const isRemoteDietaryRestrictionsEnabled = useFeatureFlag(
    'CELS-3623-remote-dietary-restrictions-enabled',
  );

  // ─── Updating Restrictions ─────────────────────────────────────────────────

  const updateRemoteRestrictions = useCallback(
    async (context: (typeof dietaryRestrictionsMachine)['context']) => {
      const restrictionsMap = context.restrictions;

      const dietaryRestrictions = Object.keys(restrictionsMap).filter(
        (restriction) => restrictionsMap[restriction as DietaryPropertyKind],
      ) as DietaryRestriction[];

      const { error } = await updateCustomerPreferences({
        dietaryRestrictions,
      });

      if (error) {
        addNoticeBanner({
          text: formatMessage(messages.updateRestrictionsFailure),
          palette: 'caution',
        });

        return { success: false };
      }

      addNoticeBanner({
        text: formatMessage(messages.updateRestrictionsSuccess),
        palette: 'success',
      });

      return { success: true };
    },
    [addNoticeBanner, formatMessage, updateCustomerPreferences],
  );

  // ─── Handle Local Storage ──────────────────────────────────────────────────

  const clearLocalStorage = useCallback(async () => {
    await AsyncStorage.removeItem(HAS_OPENED_DIETARY_PREFERENCES_MODAL_STORAGE_KEY);

    // Once we have remote dietary restrictions, they can be removed from local storage on logout.
    if (!isRemoteDietaryRestrictionsEnabled) return;

    await AsyncStorage.removeItem(DIETARY_RESTRICTIONS_STORAGE_KEY);
  }, [isRemoteDietaryRestrictionsEnabled]);

  // ───────────────────────────────────────────────────────────────────────────

  const interpreter = useInterpret(dietaryRestrictionsMachine, {
    actions: {
      onConfirmRestrictions: setMachinePersistedState,
      clearLocalStorage,
    },
    services: {
      updateRemoteRestrictions,
    },
  });

  useDietaryRestrictionsFromAsyncStorage(interpreter);

  return interpreter;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  updateRestrictionsSuccess: {
    defaultMessage: 'Your dietary restrictions are saved!',
    description: 'Dietary Restrictions | Update | Success',
  },
  updateRestrictionsFailure: {
    defaultMessage: 'Something went wrong updating your dietary restrictions.',
    description: 'Dietary Restrictions | Update | Failure',
  },
});
